// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext.js';

interface PermissionContextValue {
  /** What the person may do: the role's grants and any live delegated ones. */
  permissions: string[];
  /**
   * Sprint 63.29.0 (#579) — what the person holds in their OWN right (the
   * role), without delegated grants. Anything that grants a permission onward
   * (a role, an API key, a delegation) offers only these, because the server
   * refuses a borrowed grant there.
   */
  ownPermissions: string[];
  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (perms: string[]) => boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const ownPermissions = user?.permissions || [];
  const delegated = user?.delegated_permissions;

  const value = useMemo<PermissionContextValue>(() => {
    const permissions =
      delegated && delegated.length > 0
        ? [...new Set([...ownPermissions, ...delegated])]
        : ownPermissions;
    return {
      permissions,
      ownPermissions,
      hasPermission: (perm: string) => permissions.includes(perm),
      hasAnyPermission: (perms: string[]) => perms.some((p) => permissions.includes(p)),
    };
  }, [ownPermissions, delegated]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissionContext(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissionContext must be used within PermissionProvider');
  return ctx;
}

/**
 * Sprint 63.29.0 (#579, #595) — the viewer's OWN grants (never a delegated one),
 * or `null` when no `PermissionProvider` is mounted.
 *
 * For the "grant it onward" forms (roles, delegations, credentials), which must
 * offer exactly what the server accepts. Non-throwing so a component tested
 * without a provider renders (offering nothing) instead of crashing; the hook
 * order is unchanged because `usePermissionContext` always calls `useContext`
 * before it throws.
 */
export function useOwnPermissions(): string[] | null {
  try {
    const ctx = usePermissionContext();
    return ctx.ownPermissions ?? ctx.permissions;
  } catch {
    return null;
  }
}
